// ═══════════════════════════════════════════════════════════════════════════
// FILE: src/app/services/message-socket.service.ts
// DATE: 2026-08-08
// PURPOSE: EINE WebSocket-Verbindung für die ganze App — die Live-Zustellung
//          von Nachrichten aus dem 2do-Bus.
//
// BACKEND: wss://api.<org>.ceeq.it/ws   (nodenanomvp/src/utils/ws/wsGateway.js)
//
// ─── DREI ENTWURFSENTSCHEIDUNGEN, DIE MAN KENNEN MUSS ─────────────────────
//
// 1) EINE VERBINDUNG, NICHT EINE JE SEITE.  providedIn:'root' — der Dienst
//    lebt so lange wie die App. Würde jede Seite ihre eigene aufmachen, hätte
//    ein Nutzer mit drei Reitern neun Sockets, und der Server müsste jede
//    Nachricht neunfach senden. Wer zuhören will, abonniert `messages$`.
//
// 2) KEIN TOKEN, KEINE ANMELDEDATEN IM CODE.  Der Handshake ist ein normaler
//    HTTP-Request an dieselbe Herkunft wie das API, also schickt der Browser
//    `ceeq_token` von selbst mit. Ein Token in der URL landete sonst in jedem
//    Proxy-Log. Deshalb steht hier NICHTS über Anmeldung — das ist Absicht.
//
// 3) DER SERVER ENTSCHEIDET, WAS UNS ERREICHT.  Es gibt kein „abonniere
//    Projekt X". Der Client kann nur `ping` senden. Sonst wäre „zeig mir
//    alles" eine Zeile im Browser-Client.
//
// ─── RESSOURCEN ───────────────────────────────────────────────────────────
//   ▸ Wiederverbinden mit wachsendem Abstand + Zufallsanteil (Jitter). Ohne
//     Jitter kämen nach einem Server-Neustart alle Clients im selben
//     Sekundentakt zurück — ein selbstgebauter Ansturm.
//   ▸ Bei 4401 (abgelehnt) wird NICHT wiederholt. Der Server sagt zu Recht
//     Nein; ein Hämmern dagegen bringt niemanden zurück, es kostet nur.
//   ▸ Im Hintergrundreiter wird nicht wiederverbunden — erst wenn die Seite
//     wieder sichtbar ist. Spart Funk und Akku auf Mobilgeräten.
//
// VERWANDT: types/ceeq-message.ts · services/messages.service.ts
//           pages/messages/messages.component.ts
// ═══════════════════════════════════════════════════════════════════════════

import { Injectable, signal, computed, NgZone, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { CeeqMessage, SocketFrame } from '../types/ceeq-message';

export type SocketState = 'idle' | 'connecting' | 'open' | 'retrying' | 'rejected';

/** Obergrenze des Wiederverbindungsabstands. */
const MAX_BACKOFF_MS = 30_000;

/** WebSocket-Schliesscodes, nach denen ein erneuter Versuch sinnlos ist. */
const FATAL_CLOSE_CODES = new Set([4401, 4403]);

@Injectable({ providedIn: 'root' })
export class MessageSocketService {
  private zone = inject(NgZone);

  private socket: WebSocket | null = null;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private wantOpen = false;
  /**
   * Wurde auf DIESER Verbindung je ein `ws.hello` empfangen?
   *
   * Task #404. Der einzige Beleg dafür, dass der Server die Sitzung geprüft und
   * angenommen hat. Entscheidet in onclose [S-04] zwischen „Handshake
   * gescheitert, endgültig" und „war offen, also neu verbinden".
   * Wird bei jedem Verbindungsversuch in open() zurückgesetzt.
   */
  private helloEmpfangen = false;

  /** Live eintreffende Nachrichten. Nur `message.new`, nichts Technisches. */
  private readonly incoming = new Subject<CeeqMessage>();
  readonly messages$ = this.incoming.asObservable();

  // ── Zustand als Signale, damit Vorlagen ihn direkt binden können ────────
  readonly state = signal<SocketState>('idle');
  readonly myUuid = signal<string | null>(null);
  readonly myEmail = signal<string | null>(null);
  readonly lastError = signal<string | null>(null);

  readonly connected = computed(() => this.state() === 'open');

  constructor() {
    // Rückkehr in den Vordergrund ist der natürliche Zeitpunkt für einen
    // neuen Versuch — der Nutzer schaut gerade hin.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.wantOpen && !this.isLive()) {
        console.log('[MessageSocket] Reiter wieder sichtbar → Verbindungsversuch');
        this.scheduleRetry(0);
      }
    });
  }

  // ── Öffentliche Steuerung ────────────────────────────────────────────────

  /** Verbindung aufbauen. Mehrfachaufruf ist harmlos. */
  connect(): void {
    this.wantOpen = true;
    if (this.isLive()) return;
    this.open();
  }

  /** Bewusst trennen — es wird nicht wiederverbunden. */
  disconnect(): void {
    this.wantOpen = false;
    this.clearRetry();
    this.socket?.close(1000, 'client disconnect');
    this.socket = null;
    this.state.set('idle');
    console.log('[MessageSocket] getrennt (bewusst)');
  }

  // ── Innenleben ───────────────────────────────────────────────────────────

  private isLive(): boolean {
    return !!this.socket &&
      (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING);
  }

  /**
   * wss://api.<host>/ws — dieselbe Ableitung wie in den HTTP-Diensten
   * (vgl. sprint-data.service.ts constructApiBaseUrl).
   */
  private buildUrl(): string {
    const host = window.location.hostname;
    if (host.includes('localhost')) return 'ws://localhost:3000/ws';
    return `wss://api.${host}/ws`;
  }

  private open(): void {
    const url = this.buildUrl();
    this.state.set('connecting');
    console.log(`[MessageSocket] verbinde → ${url} (Versuch ${this.attempt + 1})`);

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.error('[MessageSocket] Aufbau fehlgeschlagen:', err);
      this.scheduleRetry();
      return;
    }
    this.socket = ws;

    // ── ÄNDERUNG 2026-08-16 · Task #404 ──────────────────────────────────────
    //   Bei jedem neuen Verbindungsversuch zurückgesetzt. Erst der Empfang von
    //   `ws.hello` setzt ihn — siehe handleFrame(). Er ist der einzige Beleg
    //   dafür, dass die Sitzung gültig WAR, und entscheidet unten in onclose
    //   zwischen „endgültig" und „neu verbinden".
    this.helloEmpfangen = false;

    ws.onopen = () => this.zone.run(() => {
      this.attempt = 0;
      this.lastError.set(null);
      this.state.set('open');
      console.log('[MessageSocket] ✅ offen');
    });

    ws.onmessage = (ev) => this.zone.run(() => this.handleFrame(ev.data));

    ws.onclose = (ev) => this.zone.run(() => {
      this.socket = null;
      this.myUuid.set(null);
      this.myEmail.set(null);

      if (!this.wantOpen) { this.state.set('idle'); return; }

      // ── [S-04] ENDGÜLTIG ODER NEU VERBINDEN? ───────────────────────────────
      //   ÄNDERUNG 2026-08-16 · Task #404
      //
      //   VORHER stand hier: `|| ev.code === 1006`, mit der Begründung
      //   „1006 = Handshake abgelehnt. Fast immer: keine gültige Sitzung."
      //
      //   DIESE ANNAHME TRÄGT NICHT. Code 1006 heißt nur „abnormal geschlossen"
      //   und hat mindestens vier Ursachen, die sich am Code NICHT unterscheiden:
      //     a  Handshake vor dem Upgrade abgelehnt   → endgültig
      //     b  Server neu gestartet                  → bei JEDEM Redeploy
      //     c  Netzaussetzer beim Nutzer             → vorübergehend
      //     d  Proxy hat die Verbindung fallen lassen→ vorübergehend
      //
      //   IM BETRIEB BEOBACHTET am 2026-08-15: Nach dem Redeploy um 22:29 UTC
      //   blieb der Kanal des PO tot, bis er die Seite von Hand neu lud. Er
      //   bekam keine Popups mehr — und erfuhr es nicht, außer über die Konsole.
      //   Dazu die Meldung „Sitzung abgelaufen", die schlicht falsch war.
      //
      //   DIE UNTERSCHEIDUNG, die trägt: nicht der Code, sondern ob die
      //   Verbindung JE OFFEN WAR. `ws.hello` kommt nur, wenn der Server die
      //   Sitzung geprüft und angenommen hat.
      //
      //   🔴 WARUM KEINE VERSUCHSZÄHLUNG STATTDESSEN: Wer 1006 pauschal
      //      wiederholt und nach n Versuchen aufgibt, baut für jeden
      //      abgemeldeten Browserreiter n Verbindungsversuche. Bei zwanzig
      //      offenen Reitern nach einer Abmeldung sind das zwanzig mal n.
      //      Die Unterscheidung muss inhaltlich sein, nicht mengenmäßig.
      const handshakeGescheitert = !this.helloEmpfangen;

      if (FATAL_CLOSE_CODES.has(ev.code) || (ev.code === 1006 && handshakeGescheitert)) {
        this.state.set('rejected');
        this.lastError.set('Nicht angemeldet oder Sitzung abgelaufen — bitte neu anmelden.');
        console.warn(
          `[MessageSocket] ⛔ abgelehnt code=${ev.code} (ws.hello nie empfangen) ` +
          '— kein erneuter Versuch'
        );
        return;
      }

      // Ab hier: die Verbindung war offen, die Sitzung also gültig. Was immer
      // sie beendet hat, ist vorübergehend.
      this.lastError.set('Verbindung unterbrochen — wird neu aufgebaut.');
      console.warn(
        `[MessageSocket] Verbindung zu code=${ev.code} grund="${ev.reason || '—'}" ` +
        `hello=${this.helloEmpfangen ? 'ja' : 'nein'} → neuer Versuch`
      );
      this.scheduleRetry();
    });

    ws.onerror = () => { /* onclose trägt die Auswertung — hier nichts doppeln */ };
  }

  private handleFrame(raw: unknown): void {
    let frame: SocketFrame;
    try {
      frame = JSON.parse(String(raw));
    } catch {
      console.warn('[MessageSocket] unlesbarer Rahmen verworfen');
      return;
    }

    switch (frame.type) {
      case 'ws.hello':
        // Task #404: Ab hier gilt die Sitzung als vom Server angenommen. Ein
        // späterer Abbruch ist damit vorübergehend, nicht endgültig.
        this.helloEmpfangen = true;
        this.myUuid.set(frame.uuid);
        this.myEmail.set(frame.email);
        console.log(`[MessageSocket] erkannt als ${frame.email} (uuid=${frame.uuid.slice(0, 20)}…)`);
        return;

      case 'message.new': {
        const { type, ...msg } = frame;
        console.log(
          `[MessageSocket] 📨 Nachricht id=${msg.id ?? '—'} ` +
          `obligation=${msg.obligation} anchor=${msg.anchor?.type}#${msg.anchor?.id}`
        );
        this.incoming.next(msg as CeeqMessage);
        return;
      }

      case 'pong':
        return;

      default:
        console.warn('[MessageSocket] unbekannter Rahmentyp verworfen:', (frame as { type?: string }).type);
    }
  }

  private scheduleRetry(forcedDelay?: number): void {
    this.clearRetry();
    if (!this.wantOpen) return;

    // Im Hintergrund nicht versuchen — visibilitychange holt das nach.
    if (document.visibilityState === 'hidden') {
      this.state.set('retrying');
      console.log('[MessageSocket] Reiter im Hintergrund → Versuch aufgeschoben');
      return;
    }

    this.attempt++;
    // Verdopplung + bis zu 30 % Zufall, damit nach einem Server-Neustart nicht
    // alle Clients gleichzeitig zurückkommen.
    const base = Math.min(MAX_BACKOFF_MS, 1000 * Math.pow(2, this.attempt - 1));
    const delay = forcedDelay ?? Math.round(base * (1 + Math.random() * 0.3));

    this.state.set('retrying');
    console.log(`[MessageSocket] neuer Versuch in ${Math.round(delay / 1000)}s (Versuch ${this.attempt})`);
    this.retryTimer = setTimeout(() => this.open(), delay);
  }

  private clearRetry(): void {
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
  }
}
